import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Warning, ClockCountdown, CaretDown, CaretUp, Package, User, CalendarBlank,
  MagnifyingGlass, CaretLeft, CaretRight, ArrowLeft, Lightning,
} from '@phosphor-icons/react';
import { ModuleShell, SkeletonBlock } from '../../../components/module-ui';
import { apiPath } from '../../../config/apiBase';
import { authHeaders } from '../api/ticketsApi';

const TICKETS_API = apiPath('/api/tickets');
const PAGE_SIZE = 10;

const STATE_COLORS = {
  breached: { bg: '#FEE2E2', fg: '#B91C1C', border: '#FECACA' },
  at_risk: { bg: '#FEF3C7', fg: '#B45309', border: '#FDE68A' },
  assignment_breached: { bg: '#FCE7F3', fg: '#9D174D', border: '#FBCFE8' },
  assignment_at_risk: { bg: '#E0E7FF', fg: '#3730A3', border: '#C7D2FE' },
};

const VIEW_META = {
  active: {
    title: 'Active ticket pipeline',
    hint: 'All non-closed tickets assigned to this person',
  },
  open: {
    title: 'Open ticket pipeline',
    hint: 'Open / pending / reopen tickets for this assignee',
  },
  breached: {
    title: 'SLA breached tickets',
    hint: 'Open tickets past their resolution SLA',
  },
  at_risk: {
    title: 'SLA at-risk tickets',
    hint: 'Open tickets nearing their resolution SLA',
  },
  closed: {
    title: 'Closed tickets',
    hint: 'Closed / completed tickets for this assignee',
  },
  total: {
    title: 'All tickets',
    hint: 'Every ticket ever assigned to this person',
  },
};

function StateBadge({ state }) {
  const c = STATE_COLORS[state] || { bg: '#F3F4F6', fg: '#374151', border: '#E5E7EB' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        background: c.bg,
        color: c.fg,
        border: `1px solid ${c.border}`,
        textTransform: 'capitalize',
        whiteSpace: 'nowrap',
      }}
    >
      {(state || '').replace(/_/g, ' ')}
    </span>
  );
}

function PipelineTrack({ pins = [] }) {
  return (
    <ol className="sla-pipeline-track" aria-label="Ticket pipeline">
      {pins.map((pin, idx) => (
        <li
          key={`${pin.pin}-${idx}`}
          className={`sla-pipeline-pin${pin.critical ? ' is-critical' : ''}${pin.done ? ' is-done' : ''}`}
        >
          <div className="sla-pipeline-pin-dot" aria-hidden />
          <div className="sla-pipeline-pin-body">
            <div className="sla-pipeline-pin-label">{pin.label}</div>
            <div className="sla-pipeline-pin-what"><strong>What:</strong> {pin.what || '—'}</div>
            <div className="sla-pipeline-pin-who"><strong>Who:</strong> {pin.who || '—'}</div>
            <div className="sla-pipeline-pin-when"><strong>When:</strong> {pin.when_ist || '—'}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * Performance → assignee 360°: KPI cards drive the ticket list (same pattern as Product 360°).
 */
export default function Assignee360Page({ userId }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialView = searchParams.get('view') || 'active';

  const [person, setPerson] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(true);

  const [loadingTickets, setLoadingTickets] = useState(true);
  const [payload, setPayload] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState({});
  const [ticketError, setTicketError] = useState(null);
  const [viewMode, setViewMode] = useState(
    ['active', 'open', 'breached', 'at_risk', 'closed', 'total'].includes(initialView)
      ? initialView
      : 'active',
  );
  const [kpiOpen, setKpiOpen] = useState({ matched: 0, breached: 0, at_risk: 0 });

  useEffect(() => {
    let cancelled = false;
    setLoadingMeta(true);
    axios.get(`${TICKETS_API}/dashboard/performance`, {
      ...authHeaders(),
      params: { limit: 40 },
    }).then((res) => {
      if (cancelled) return;
      const found = (res.data?.items || []).find((i) => i.user_id === userId) || null;
      setPerson(found);
    }).catch(() => {
      if (!cancelled) setPerson(null);
    }).finally(() => {
      if (!cancelled) setLoadingMeta(false);
    });
    return () => { cancelled = true; };
  }, [userId]);

  // Baseline open / SLA counts for chips.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${TICKETS_API}/dashboard/ticket-pipeline`, {
      ...authHeaders(),
      params: {
        assigned_to: userId,
        status_group: 'open',
        page: 1,
        page_size: 1,
      },
    }).then((res) => {
      if (cancelled) return;
      const s = res.data?.summary || {};
      setKpiOpen({
        matched: s.matched || 0,
        breached: s.breached || 0,
        at_risk: s.at_risk || 0,
      });
    }).catch(() => {
      if (!cancelled) setKpiOpen({ matched: 0, breached: 0, at_risk: 0 });
    });
    return () => { cancelled = true; };
  }, [userId]);

  const fetchPipeline = useCallback(async () => {
    setLoadingTickets(true);
    setTicketError(null);
    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
        assigned_to: userId,
      };
      if (viewMode === 'closed') params.status_group = 'closed';
      else if (viewMode === 'total') params.status_group = 'all';
      else params.status_group = 'open';
      if (viewMode === 'breached') params.pipeline_state = 'breached';
      if (viewMode === 'at_risk') params.pipeline_state = 'at_risk';
      if (q.trim()) params.q = q.trim();
      const res = await axios.get(`${TICKETS_API}/dashboard/ticket-pipeline`, {
        ...authHeaders(),
        params,
      });
      setPayload(res.data);
      // Prefer performance API name; otherwise use pipeline title assignee segment.
      setPerson((prev) => {
        if (prev) return prev;
        const parts = String(res.data?.title || '').split(' · ');
        const nameGuess = parts[parts.length - 1];
        if (nameGuess && nameGuess !== userId) {
          return { user_id: userId, name: nameGuess };
        }
        return prev;
      });
    } catch (e) {
      setTicketError('Could not load tickets');
      setPayload(null);
    } finally {
      setLoadingTickets(false);
    }
  }, [userId, page, q, viewMode]);

  useEffect(() => { fetchPipeline(); }, [fetchPipeline]);

  useEffect(() => {
    setPage(1);
    setExpanded({});
  }, [userId, viewMode]);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = searchInput.trim();
      if (next === q) return;
      setPage(1);
      setQ(next);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput, q]);

  const displayName = person?.name || payload?.title || userId;
  const items = payload?.items || [];
  const pagination = payload?.pagination || {
    page: 1, page_size: PAGE_SIZE, total: 0, total_pages: 1, has_prev: false, has_next: false,
  };
  const from = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.page_size + 1;
  const to = Math.min(pagination.page * pagination.page_size, pagination.total);
  const viewMeta = VIEW_META[viewMode] || VIEW_META.active;

  const listLink = () => {
    const sp = new URLSearchParams({ assigned_to: userId });
    if (viewMode === 'closed') sp.set('status_group', 'closed');
    else if (viewMode === 'total') sp.set('status_group', 'all');
    else sp.set('status_group', 'open');
    return `/dashboard/tickets/list?${sp.toString()}`;
  };

  if (loadingMeta && !person && !payload) {
    return (
      <ModuleShell title="Assignee 360°" subtitle="Loading…">
        <SkeletonBlock rows={8} />
      </ModuleShell>
    );
  }

  return (
    <ModuleShell
      title={displayName}
      subtitle="Performance 360° — assignee workload and ticket pipeline"
      headerActions={(
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="tk-btn tk-btn-secondary" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={16} />
            Overview
          </button>
          <Link to={listLink()} className="tk-btn tk-btn-secondary">
            Ticket list
          </Link>
        </div>
      )}
    >
      <div data-testid="assignee-360-page" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 14px',
          border: '1px solid #E5E7EB',
          borderRadius: 12,
          background: '#F8FAFC',
        }}
        >
          <div style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: '#DBEAFE',
            display: 'grid',
            placeItems: 'center',
          }}
          >
            <Lightning size={20} color="#002FA7" weight="fill" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{displayName}</div>
            <div style={{ fontSize: 12, color: '#6B7280' }}>
              {[person?.designation, person?.email].filter(Boolean).join(' · ') || 'Assignee performance'}
            </div>
          </div>
        </div>

        <div className="sla-pipeline-summary" role="group" aria-label="Assignee summary">
          {[
            { id: 'active', value: person?.active ?? kpiOpen.matched, label: 'Active tickets' },
            { id: 'open', value: person?.open ?? kpiOpen.matched, label: 'Open' },
            { id: 'breached', value: kpiOpen.breached, label: 'SLA breached' },
            { id: 'at_risk', value: kpiOpen.at_risk, label: 'SLA at risk' },
            { id: 'closed', value: person?.closed ?? 0, label: 'Closed' },
            { id: 'total', value: person?.total ?? 0, label: 'Total' },
          ].map((chip) => {
            const selected = viewMode === chip.id;
            return (
              <button
                key={chip.id}
                type="button"
                className={`sla-summary-chip${selected ? ' is-selected' : ''}`}
                onClick={() => setViewMode(chip.id)}
                aria-pressed={selected}
                style={{
                  cursor: 'pointer',
                  border: selected ? '2px solid #002FA7' : undefined,
                  background: selected ? '#EFF6FF' : undefined,
                  textAlign: 'left',
                }}
              >
                <span className="sla-summary-chip-value">{chip.value}</span>
                <span className="sla-summary-chip-label">{chip.label}</span>
              </button>
            );
          })}
        </div>
        <p style={{ margin: '-8px 0 0', fontSize: 12, color: '#6B7280' }}>
          Click a card above to change the ticket list below
          {viewMode !== 'active' ? ` · showing: ${viewMeta.title}` : ''}
        </p>

        <section className="sla-pipeline-panel" style={{ margin: 0 }} data-testid="assignee-360-ticket-list">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{viewMeta.title}</h3>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6B7280' }}>{viewMeta.hint}</p>
            </div>
            {viewMode !== 'active' && (
              <button
                type="button"
                className="tk-btn tk-btn-secondary"
                onClick={() => setViewMode('active')}
              >
                Show active
              </button>
            )}
          </div>

          <div className="sla-pipeline-filters">
            <form
              className="sla-api-search"
              onSubmit={(e) => {
                e.preventDefault();
                const next = searchInput.trim();
                setPage(1);
                setQ(next);
              }}
              role="search"
            >
              <MagnifyingGlass size={16} className="sla-api-search-icon" aria-hidden />
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search ticket, product, client…"
                data-testid="ticket-pipeline-search"
              />
              <button type="submit" className="tk-btn tk-btn-secondary">Search</button>
            </form>
          </div>

          {ticketError && <p style={{ color: '#B91C1C', fontSize: 13 }}>{ticketError}</p>}
          {loadingTickets && !payload ? (
            <SkeletonBlock rows={4} />
          ) : items.length === 0 ? (
            <div className="sla-pipeline-empty">
              <ClockCountdown size={28} color="#9CA3AF" />
              <p>
                {q
                  ? `No tickets match “${q}”.`
                  : `No tickets for “${viewMeta.title.replace(/ tickets$/i, '').toLowerCase()}”.`}
              </p>
            </div>
          ) : (
            <>
              <div className="sla-pipeline-grid">
                {items.map((item) => {
                  const open = !!expanded[item.ticket_no];
                  const productName = item.product?.product_name || item.product?.product_key || '—';
                  const assignees = (item.who?.assignee_names || []).join(', ') || 'Unassigned';
                  return (
                    <div
                      key={item.ticket_no}
                      className={`sla-pipeline-card${open ? ' is-expanded' : ''}`}
                      data-testid="ticket-pipeline-row"
                    >
                      <div className="sla-pipeline-row-main">
                        <div className="sla-pipeline-row-top">
                          <Link
                            to={`/dashboard/tickets/list/${encodeURIComponent(item.ticket_no)}`}
                            className="sla-ticket-link"
                          >
                            {item.ticket_no}
                          </Link>
                          <StateBadge state={item.primary_state} />
                          {item.what?.priority && (
                            <span className={`badge badge-${item.what.priority}`}>{item.what.priority}</span>
                          )}
                        </div>
                        <div className="sla-pipeline-what" title={item.what?.subject}>
                          <strong>What:</strong> {item.what?.subject || '—'}
                        </div>
                        <div className="sla-pipeline-meta">
                          <span><User size={14} /> <strong>Who:</strong> {assignees}</span>
                          <span><CalendarBlank size={14} /> <strong>When:</strong> {item.when?.due_date_ist || item.when?.created_at_ist || '—'}</span>
                          <span><Package size={14} /> <strong>App:</strong> {productName}</span>
                          <span><Warning size={14} /> <strong>Client:</strong> {item.what?.client || '—'}</span>
                        </div>
                      </div>
                      <div className="sla-pipeline-row-actions">
                        <button
                          type="button"
                          className="tk-btn tk-btn-secondary"
                          onClick={() => setExpanded((prev) => ({ ...prev, [item.ticket_no]: !prev[item.ticket_no] }))}
                          aria-expanded={open}
                        >
                          {open ? <CaretUp size={16} /> : <CaretDown size={16} />}
                          Stages
                        </button>
                      </div>
                      {open && (
                        <div className="sla-pipeline-detail">
                          <div className="sla-pipeline-detail-grid">
                            <div>
                              <h4>Reporter</h4>
                              <p>{item.who?.reporter_name || '—'}{item.who?.reporter_email ? ` · ${item.who.reporter_email}` : ''}</p>
                            </div>
                            <div>
                              <h4>Domain</h4>
                              <p>{item.what?.domain || '—'} · {item.what?.status || '—'}</p>
                            </div>
                            <div>
                              <h4>Created</h4>
                              <p>{item.when?.created_at_ist || '—'} · {item.who?.created_by || '—'}</p>
                            </div>
                            <div>
                              <h4>SLA</h4>
                              <p>
                                {item.sla_policy?.name || '—'}
                                {item.when?.due_date_ist ? ` · due ${item.when.due_date_ist}` : ''}
                              </p>
                            </div>
                          </div>
                          <PipelineTrack pins={item.pipeline || []} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="sla-pagination" data-testid="ticket-pipeline-pagination">
                <span className="sla-pagination-meta">
                  Showing {from}–{to} of {pagination.total}
                  {q ? ` for “${q}”` : ''}
                </span>
                <div className="sla-pagination-buttons">
                  <button
                    type="button"
                    className="tk-btn tk-btn-secondary"
                    disabled={!pagination.has_prev || loadingTickets}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <CaretLeft size={16} />
                    Previous
                  </button>
                  <span className="sla-pagination-page">
                    Page {pagination.page} / {pagination.total_pages}
                  </span>
                  <button
                    type="button"
                    className="tk-btn tk-btn-secondary"
                    disabled={!pagination.has_next || loadingTickets}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                    <CaretRight size={16} />
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </ModuleShell>
  );
}
